import { useState } from "react";
import axios from "axios";
import { Mail, Phone, MessageCircle, Send, CheckCircle2, Loader2 } from "lucide-react";
import PageHead from "@/components/PageHead";
import Reveal from "@/components/Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const INTERESTS = [
  "Consultoria & Auditoria de TI",
  "Servidores Windows & Linux",
  "Active Directory & Governança",
  "Virtualização Hyper-V",
  "Nuvem Azure & Microsoft 365",
  "Redes, Firewalls & VPNs",
  "Backup & Continuidade",
  "Outro assunto",
];

const CHANNELS = [
  {
    icon: MessageCircle,
    label: "WhatsApp",
    value: "+55 (21) 99159-2603",
    href: "https://wa.me/5521991592603",
    external: true,
    testId: "contact-channel-whatsapp",
  },
  { icon: Mail, label: "E-mail", value: "contato@iprsinf.com.br", href: "mailto:contato@iprsinf.com.br", testId: "contact-channel-email" },
  { icon: Phone, label: "Telefone", value: "+55 (21) 99159-2603", href: "tel:+5521991592603", testId: "contact-channel-phone" },
];

const EMPTY = {
  name: "",
  company: "",
  email: "",
  phone: "",
  service_interest: INTERESTS[0],
  needs: "",
  consent: false,
};

export default function Contact() {
  const [form, setForm] = useState(EMPTY);
  const [status, setStatus] = useState("idle");
  const [error, setError] = useState("");

  const update = (field) => (e) => {
    const value = field === "consent" ? e.target.checked : e.target.value;
    setForm((f) => ({ ...f, [field]: value }));
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    setStatus("sending");
    setError("");
    try {
      await axios.post(`${API}/contact`, form, { timeout: 30000 });
      setStatus("success");
      setForm(EMPTY);
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(typeof detail === "string" ? detail : "Não foi possível enviar agora. Tente novamente em instantes.");
      setStatus("error");
    }
  };

  const inputCls =
    "w-full bg-white border border-line rounded-md px-4 py-3 text-sm text-graphite placeholder:text-steel/50 focus:border-techblue focus:ring-1 focus:ring-techblue outline-none transition-colors duration-200";

  return (
    <div>
      <PageHead
        title="Fale com um Especialista em TI | IPRS Soluções em TI"
        description="Solicite uma avaliação técnica de infraestrutura. Preencha o formulário e um consultor sênior entrará em contato."
        path="/contato"
      />

      <section className="relative pt-28 lg:pt-40 pb-12 overflow-hidden">
        <div className="absolute inset-0 grid-lines" aria-hidden="true" />
        <div className="container-x relative">
          <Reveal>
            <p className="text-xs font-semibold uppercase tracking-[0.2em] text-techblue">Contato</p>
          </Reveal>
          <Reveal delay={0.08}>
            <h1 className="mt-4 text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-graphite leading-[1.15] max-w-3xl">
              Solicite uma avaliação técnica de infraestrutura
            </h1>
          </Reveal>
          <Reveal delay={0.16}>
            <p className="mt-6 text-base lg:text-lg text-steel leading-relaxed max-w-2xl">
              Preencha o formulário abaixo e nossa equipe entrará em contato para entender o
              cenário da sua operação e agendar a avaliação.
            </p>
          </Reveal>
        </div>
      </section>

      <section className="pb-20 sm:pb-24">
        <div className="container-x grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
          <Reveal className="lg:col-span-7">
            <div className="bg-white border border-line rounded-xl p-6 sm:p-10">
              {status === "success" ? (
                <div data-testid="contact-form-success" className="text-center py-14">
                  <CheckCircle2 className="w-12 h-12 text-techblue mx-auto" />
                  <h2 className="mt-5 text-xl font-semibold text-graphite">Mensagem enviada com sucesso</h2>
                  <p className="mt-3 text-sm text-steel leading-relaxed max-w-md mx-auto">
                    Obrigado pelo contato. Nossa equipe analisará sua mensagem e retornará em
                    breve no e-mail informado.
                  </p>
                  <button
                    type="button"
                    data-testid="contact-form-reset"
                    onClick={() => setStatus("idle")}
                    className="mt-8 inline-flex items-center gap-1.5 border border-graphite/20 hover:border-techblue hover:text-techblue text-graphite text-sm font-semibold px-6 py-3 rounded-md transition-colors duration-200"
                  >
                    Enviar outra mensagem
                  </button>
                </div>
              ) : (
                <form onSubmit={onSubmit} data-testid="contact-form" className="space-y-5" noValidate={false}>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                    <div>
                      <label htmlFor="contact-name" className="block text-sm font-medium text-graphite mb-1.5">
                        Nome completo *
                      </label>
                      <input
                        id="contact-name"
                        type="text"
                        required
                        minLength={2}
                        value={form.name}
                        onChange={update("name")}
                        placeholder="Seu nome"
                        data-testid="contact-input-name"
                        className={inputCls}
                      />
                    </div>
                    <div>
                      <label htmlFor="contact-company" className="block text-sm font-medium text-graphite mb-1.5">
                        Empresa *
                      </label>
                      <input
                        id="contact-company"
                        type="text"
                        required
                        minLength={2}
                        value={form.company}
                        onChange={update("company")}
                        placeholder="Nome da sua empresa"
                        data-testid="contact-input-company"
                        className={inputCls}
                      />
                    </div>
                    <div>
                      <label htmlFor="contact-email" className="block text-sm font-medium text-graphite mb-1.5">
                        E-mail corporativo *
                      </label>
                      <input
                        id="contact-email"
                        type="email"
                        required
                        value={form.email}
                        onChange={update("email")}
                        placeholder="nome@suaempresa.com.br"
                        data-testid="contact-input-email"
                        className={inputCls}
                      />
                    </div>
                    <div>
                      <label htmlFor="contact-phone" className="block text-sm font-medium text-graphite mb-1.5">
                        Telefone / WhatsApp *
                      </label>
                      <input
                        id="contact-phone"
                        type="tel"
                        required
                        minLength={8}
                        value={form.phone}
                        onChange={update("phone")}
                        placeholder="(11) 99999-9999"
                        data-testid="contact-input-phone"
                        className={inputCls}
                      />
                    </div>
                  </div>

                  <div>
                    <label htmlFor="contact-service" className="block text-sm font-medium text-graphite mb-1.5">
                      Interesse principal *
                    </label>
                    <select
                      id="contact-service"
                      required
                      value={form.service_interest}
                      onChange={update("service_interest")}
                      data-testid="contact-select-service"
                      className={inputCls}
                    >
                      {INTERESTS.map((i) => (
                        <option key={i} value={i}>
                          {i}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label htmlFor="contact-needs" className="block text-sm font-medium text-graphite mb-1.5">
                      Descrição da necessidade / cenário atual *
                    </label>
                    <textarea
                      id="contact-needs"
                      required
                      minLength={10}
                      rows={5}
                      value={form.needs}
                      onChange={update("needs")}
                      placeholder="Conte-nos brevemente sobre sua infraestrutura atual e objetivos..."
                      data-testid="contact-input-needs"
                      className={`${inputCls} resize-y`}
                    />
                  </div>

                  <label className="flex items-start gap-2.5 text-sm text-steel cursor-pointer">
                    <input
                      type="checkbox"
                      required
                      checked={form.consent}
                      onChange={update("consent")}
                      data-testid="contact-consent-checkbox"
                      className="mt-0.5 w-4 h-4 accent-[#0788F8]"
                    />
                    <span>
                      Autorizo o contato da IPRS e li a{" "}
                      <a href="/politica-de-privacidade" data-testid="contact-privacy-link" className="text-techblue font-medium hover:underline">
                        Política de Privacidade
                      </a>
                      . *
                    </span>
                  </label>

                  {status === "error" && (
                    <p data-testid="contact-form-error" role="alert" className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-md px-4 py-3">
                      {error}
                    </p>
                  )}

                  <button
                    type="submit"
                    data-testid="contact-form-submit"
                    disabled={status === "sending"}
                    className="inline-flex items-center justify-center gap-2 bg-techblue hover:bg-techblue-hover disabled:opacity-60 disabled:cursor-not-allowed text-white text-sm font-semibold px-8 py-3.5 rounded-md transition-colors duration-200"
                  >
                    {status === "sending" ? (
                      <>
                        <Loader2 className="w-4 h-4 animate-spin" />
                        Enviando...
                      </>
                    ) : (
                      <>
                        Enviar mensagem
                        <Send className="w-4 h-4" />
                      </>
                    )}
                  </button>
                </form>
              )}
            </div>
          </Reveal>

          <Reveal delay={0.1} className="lg:col-span-5">
            <div className="bg-graphite rounded-xl p-8 sm:p-10 text-white">
              <h2 className="text-xl font-semibold">Canais diretos</h2>
              <p className="mt-3 text-sm text-white/60 leading-relaxed">
                Prefere um canal imediato? Fale diretamente com nossa equipe pelos canais abaixo.
              </p>
              <ul className="mt-8 space-y-6">
                {CHANNELS.map((c) => {
                  const cls = "flex items-start gap-3.5 group";
                  const content = (
                    <>
                      <span className="w-10 h-10 rounded-lg bg-white/10 flex items-center justify-center text-techblue group-hover:bg-techblue group-hover:text-white transition-colors duration-200 shrink-0">
                        <c.icon className="w-5 h-5" />
                      </span>
                      <div>
                        <p className="text-sm font-semibold">{c.label}</p>
                        <p className="text-sm text-white/70 mt-0.5 group-hover:text-white transition-colors duration-200">{c.value}</p>
                      </div>
                    </>
                  );
                  return (
                    <li key={c.label} data-testid={c.testId}>
                      {c.href ? (
                        <a
                          href={c.href}
                          data-testid={`${c.testId}-link`}
                          className={cls}
                          {...(c.external ? { target: "_blank", rel: "noopener noreferrer" } : {})}
                        >
                          {content}
                        </a>
                      ) : (
                        <div className={cls}>{content}</div>
                      )}
                    </li>
                  );
                })}
              </ul>
              <div className="mt-10 pt-8 border-t border-white/10">
                <p className="text-sm text-white/60 leading-relaxed">
                  Atendemos empresas de todos os portes, com foco em ambientes corporativos
                  que exigem disponibilidade e segurança.
                </p>
              </div>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
